'use client';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { formatLarge } from '@benzinga/utils';
import type { Financials } from '@benzinga/securities-manager';
import { SectionTitle, StatBoxesContainer } from '@benzinga/core-ui';

import type { BaseQuotePageProps } from '../../../../src/utils/quote/types';
import { quoteHeadingFromMetaTitle } from '../../../../src/utils/quote/helpers';
import type { EarningsSummary } from '../../../../src/utils/quote/summaries';

import { StatBox } from '../../../../src/components/Quote/StatBox';
import DrilldownFooter from '../../../../src/components/Quote/DrilldownFooter';
import { EarningsFAQ } from '../../../../src/components/Quote/FAQ/EarningsFAQ';
import QuoteCalendar from '../../../../src/components/Quote/QuoteCalendar';
import ZacksHeadScript from '../../../../src/components/Quote/ZacksHeadScript';
import EarningsChart from '../../../../src/components/Quote/EarningsCharts';
import { QuoteFooter } from '../../../../src/components/Quote/QuoteFooter';
import EarningsOverview from '../../../../src/components/Quote/EarningsOverview';

const RaptiveAdPlaceholder = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.RaptiveAdPlaceholder };
  }),
);

interface EarningsContentProps extends BaseQuotePageProps {
  earningsSummary: EarningsSummary | null;
  fundamentalsAnnual: Financials | null;
}

const formatDate = (date: string, language: string) => {
  return date ? DateTime.fromISO(date).setLocale(language).toFormat('MMM d') : 'N/A';
};

const EarningsSummaryBox: React.FC<{
  earningsSummary?: EarningsSummary | null;
  fundamentalsAnnual?: Financials | null;
}> = ({ earningsSummary, fundamentalsAnnual }) => {
  const { t, i18n } = useTranslation('quote');
  const isEstimateAvalible = earningsSummary?.next?.eps_est ? true : false;
  const relevantEarnings = isEstimateAvalible ? earningsSummary?.next : earningsSummary?.last;
  const financialsAnnual = fundamentalsAnnual?.financials?.[0];
  const totalRevenue = financialsAnnual?.incomeStatement?.totalRevenue;
  return (
    <StatBoxesContainer>
      {relevantEarnings && (
        <StatBox
          title={t('Quote.Earnings.earnings-date')}
          value={relevantEarnings ? formatDate(relevantEarnings.date, i18n.language) : '–'}
        />
      )}
      {isEstimateAvalible ? (
        <>
          <StatBox
            title={t('Quote.Earnings.eps-estimate')}
            value={relevantEarnings?.eps_est ? `$${relevantEarnings.eps_est}` : '–'}
          />
          <StatBox
            title={t('Quote.Earnings.quarterly-revenue-estimate')}
            value={relevantEarnings?.revenue_est ? `$${formatLarge(Number(relevantEarnings.revenue_est))}` : '–'}
          />
        </>
      ) : (
        <>
          <StatBox title={t('Quote.Earnings.eps')} value={relevantEarnings?.eps ? `$${relevantEarnings.eps}` : '–'} />
          <StatBox
            title={t('Quote.Earnings.quarterly-revenue')}
            value={relevantEarnings?.revenue ? `$${formatLarge(Number(relevantEarnings.revenue))}` : '–'}
          />
        </>
      )}
      <StatBox
        title={
          financialsAnnual?.asOf
            ? `${t('Quote.Earnings.annual-revenue')}<br />(${t('Helpers.as-of')} ${formatDate(financialsAnnual?.asOf, i18n.language)})`
            : t('Quote.Earnings.annual-revenue')
        }
        value={totalRevenue ? `$${formatLarge(totalRevenue)}` : '–'}
      />
    </StatBoxesContainer>
  );
};

const EarningsContent: React.FC<EarningsContentProps> = ({
  earningsSummary,
  fundamentalsAnnual,
  metaProps,
  profile,
  symbol,
}) => {
  const { t } = useTranslation(['quote', 'common']);

  return (
    <>
      <ZacksHeadScript />
      <div className="main-div flex flex-col gap-y-8">
        <div className="flex flex-col lg:flex-row">
          <div className="flex flex-col gap-y-8">
            <div className="w-full md:flex">
              <div className="w-full">
                <SectionTitle level={2} uppercase={false}>
                  {quoteHeadingFromMetaTitle(metaProps.title)}
                </SectionTitle>
                <EarningsOverview earningsSummary={earningsSummary} quote={profile?.richQuoteData} />
                <EarningsSummaryBox
                  earningsSummary={earningsSummary ?? null}
                  fundamentalsAnnual={fundamentalsAnnual ?? null}
                />
              </div>
              <Suspense fallback={<div />}>
                <RaptiveAdPlaceholder
                  className="w-[300px] min-w-[300px] h-[250px] md:ml-4"
                  disableMargins={true}
                  onlyDesktop={true}
                  type="static-sidebar"
                />
              </Suspense>
            </div>
            <div>
              <SectionTitle level={3} size="xl" uppercase={false}>
                {t('Quote.Earnings.earnings-history-title', { companyName: profile?.richQuoteData?.name })}
              </SectionTitle>
              <p className="mt-2 mb-4 text-sm text-gray-600">
                {t('Quote.Earnings.analyze-earnings', { companyName: profile?.richQuoteData?.name })}
              </p>
              <QuoteCalendar calendar="earnings" hideFilters initialData={earningsSummary?.earnings} symbol={symbol} />
            </div>
            <div>
              <SectionTitle level={3} size="xl" uppercase={false}>
                {t('Quote.Earnings.Charts.company-earnings-per-share', {
                  companyName: profile?.richQuoteData?.name,
                  symbol: symbol,
                })}
              </SectionTitle>
              <p className="mt-2 mb-4 text-sm text-gray-600">
                {t('Quote.Earnings.Charts.eps-description', { companyName: profile?.richQuoteData?.name })}
              </p>
              <React.Suspense fallback={<div />}>
                <EarningsChart data={earningsSummary?.earnings ?? []} type="eps" />
              </React.Suspense>
            </div>
            <div>
              <SectionTitle level={3} size="xl" uppercase={false}>
                {t('Quote.Earnings.Charts.company-revenue', {
                  companyName: profile?.richQuoteData?.name,
                  symbol: symbol,
                })}
              </SectionTitle>
              <p className="mt-2 mb-4 text-sm text-gray-600">
                {t('Quote.Earnings.Charts.revenue-description', { companyName: profile?.richQuoteData?.name })}
              </p>
              <React.Suspense fallback={<div />}>
                <EarningsChart data={earningsSummary?.earnings ?? []} type="revenue" />
              </React.Suspense>
            </div>
          </div>
        </div>
        <RaptiveAdPlaceholder className="w-[300px] min-w-[300px]" onlyMobile={true} type="content-small" />
        <DrilldownFooter
          isETF={profile.isETF}
          leftSection={
            <>
              <SectionTitle level={3} size="xl" uppercase={false}>
                {t('Quote.FAQ.faq')}
              </SectionTitle>
              <EarningsFAQ earningsSummary={earningsSummary ?? null} quote={profile?.richQuoteData} />
            </>
          }
          peers={profile.peers}
          peerSymbols={profile.peerSymbols}
          symbol={symbol}
          type={profile.schedule?.type ?? null}
        />
        <QuoteFooter activeTab="earnings" showAttribution={false} />
      </div>
    </>
  );
};

export default EarningsContent;
